<template>
  <div>
    <DataTable stripedRows
               class="contracts-table"
               v-bind="$props"
               @page="onPage($event)"
               @filter="onFilter($event)"
               v-model:filters="filters"
               v-model:expandedRows="expandedRows"
               :filterDisplay="filterDisplay">
        <TableColumn :expander="true" headerStyle="width: 3rem"/>
        <TableColumn field="contract_date" :header="this.$t('contracts.contract_date')">
            <template #filter="{filterModel, filterCallback}">
                <DatePicker v-model="filterModel.value"
                            range
                            :enable-time-picker="false"
                            :placeholder="$t('contracts.filters.contract_date')"
                            @update:model-value="onContractDateFilterChange($event, filterCallback)"/>
            </template>
            <template #body="slotProps">
                {{ formatDate(slotProps.data.contract_date, 'short2') }}
            </template>
        </TableColumn>
        <TableColumn field="client_id" style="min-width: 220px" :header="this.$t('clients.client')">
            <template #filter="{filterModel, filterCallback}">
                <Multiselect v-if="!client_id"
                             class="p-0"
                             :searchable="true"
                             :options="clientOptions"
                             v-model="filterModel.value"
                             :placeholder="$t('contracts.filters.client')"
                             @search-change="fetchClients"
                             @change="filterCallback()"/>
            </template>
            <template #body="{ data }">
                {{ client(data.client_id)?.kdnr }}
                {{ client(data.client_id)?.name }}
            </template>
        </TableColumn>
        <TableColumn field="title" :header="this.$t('contracts.title')">
            <template #filter="{filterModel, filterCallback}">
                <input type="text"
                       v-model="filterModel.value"
                       :placeholder="$t('contracts.title')"
                       @input="debouncedFilter(filterCallback)"
                       class="form-control"/>
            </template>
            <template #body="{ data }">
                <router-link :to="{name: ROUTES.CONTRACTS.SHOW, params: {id: data.id }}">
                    {{ data.title }}
                </router-link>
            </template>
        </TableColumn>
        <TableColumn field="contract_status_ids" style="min-width: 250px" :header="this.$t('contracts.contract_status')">
            <template #filter="{filterModel, filterCallback}">
                <Multiselect class="p-0"
                             mode="multiple"
                             valueProp="id"
                             label="name"
                             :closeOnSelect="false"
                             :canClear="true"
                             :options="setupStore.contractStatuses"
                             v-model="filterModel.value"
                             :placeholder="$t('contracts.filters.contract_status')"
                             @change="filterCallback()"/>
            </template>
            <template #body="slotProps">
                {{ setupStore.getContractStatusById(slotProps.data.contract_status_id)?.name }}
            </template>
        </TableColumn>
        <TableColumn field="info" :header="this.$t('contracts.info')">
            <template #filter="{filterModel, filterCallback}">
                <input type="text"
                       v-model="filterModel.value"
                       :placeholder="$t('contracts.info')"
                       @input="debouncedFilter(filterCallback)"
                       class="form-control"/>
            </template>
            <template #body="slotProps">
                {{ slotProps.data.info }}
            </template>
        </TableColumn>
        <TableColumn :header="this.$t('clients.operations')">
            <template #body="slotProps">
                <div class="operations-wrapper">
                    <router-link class="operation-item" :to="{name: ROUTES.CONTRACTS.SHOW, params: {id: slotProps.data.id}}"
                                 :title="$t('contracts.show')">
                        <i class="fas fa-eye"/>
                    </router-link>
                    <router-link class="operation-item" :to="{name: ROUTES.CONTRACTS.EDIT, params: {id: slotProps.data.id}}"
                                 :title="$t('contracts.edit.headline')">
                        <i class="fas fa-edit"/>
                    </router-link>
                </div>
        </template>
      </TableColumn>
      <template #expansion="slotProps">
        <div class="ms-5">
          <ProductsTable :value="slotProps.data.products"></ProductsTable>
        </div>
      </template>
    </DataTable>
  </div>
</template>

<script>
import {useSetupStore} from "@/stores/setup";
import ProductsTable from "@/components/tables/ProductsTable.vue";
import {useClientStore} from "@/stores/client";
import {ROUTES} from "@/scripts/constants";
import {useContractStore} from "@/stores/contract";
import {FilterMatchMode} from "primevue/api";
import Multiselect from "@vueform/multiselect/src/Multiselect.vue";
import dayjs from "dayjs";

const FILTER_QUERY_KEYS = [
    'client_id',
    'contract_date_from',
    'contract_date_to',
    'contract_status_ids',
    'info',
    'title',
]

export default {
  name: 'ContractsTable',
  computed: {
    ROUTES() {
      return ROUTES
    },
    clientOptions() {
        return this.clientSearchResults.map((client) => ({
            label: `${client.kdnr} - ${client.name}`,
            value: client.id,
        }))
    },
    filterDisplay() {
        return this.showFilters ? 'row' : null
    }
  },
  setup() {
    return {
        clientStore: useClientStore(),
        contractStore: useContractStore(),
        setupStore: useSetupStore(),
    }
  },
  components: {
    Multiselect,
    ProductsTable,
  },
  props: {
    client_id: {
      type: Number,
      default: null,
    },
    showFilters: {
      type: Boolean,
      default: false,
    },
    value: {
      type: Array,
      default: () => [],
    },
    rowsPerPageOptions: {
      type: Array,
      default: () => useSetupStore().dataTableDefaults.rowsPerPageOptions,
    },
    paginator: {
      type: Boolean,
      default: false,
    },
    lazy: {
      type: Boolean,
      default: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    rows: {
      type: Number,
      default: null,
    },
    totalRecords: {
      type: Number,
      default: null,
    },
    paginatorTemplate: {
      type: String,
      default: null,
    },
    dataKey: {
      type: String,
      default: null,
    },
    currentPageReportTemplate: {
      type: String,
      default: null,
    },
    responsiveLayout: {
      type: String,
      default: 'scroll', // wide tables must scroll inside their box, not push the page sideways on phones
    },
  },
  data() {
    return {
      expandedRows: [],
      clientSearchResults: [],
      filterTimer: null,
      filters: {
        client_id: {value: null, matchMode: FilterMatchMode.EQUALS},
        contract_date: {value: null, matchMode: FilterMatchMode.BETWEEN},
        contract_status_ids: {value: [], matchMode: FilterMatchMode.IN},
        info: {value: null, matchMode: FilterMatchMode.CONTAINS},
        title: {value: null, matchMode: FilterMatchMode.CONTAINS},
      },
    }
  },
    created() {
        this.setupStore.getContractStatuses()
        this.setupStore.getProductStatuses()
        this.setupStore.getProductTypes()
        if (this.showFilters) {
            this.restoreFiltersFromUrl()
            this.contractStore.serverOptions.page = 0
            this.contractStore.serverOptions.filters = this.filters
            this.getContracts()
        }
    },
    beforeUnmount() {
        clearTimeout(this.filterTimer)
    },
    methods: {
        client(clientId) {
            return this.clientStore.findClient(clientId)
        },
        debouncedFilter(filterCallback) {
            clearTimeout(this.filterTimer)
            this.filterTimer = setTimeout(filterCallback, 400)
        },
        fetchClients(query) {
            if (query) {
                this.clientStore.search(query).then(({data}) => {
                    this.clientSearchResults = data.clients
                })
            }
        },
        getContracts() {
            this.contractStore.index(this.client_id)
        },
        onContractDateFilterChange(value, filterCallback) {
            if (!value || value.length === 0 || (value[0] && value[1])) {
                filterCallback()
            }
        },
        onFilter({filters}) {
            this.filters = filters
            this.contractStore.serverOptions.page = 0
            this.contractStore.serverOptions.filters = filters
            this.syncFiltersToUrl()
            this.getContracts()
        },
        onPage(event) {
            this.contractStore.serverOptions = {
                ...this.contractStore.serverOptions,
                ...event,
                filters: this.filters,
            }
            this.getContracts()
        },
        restoreFiltersFromUrl() {
            const query = this.$route.query
            if (!this.client_id && query.client_id) {
                const clientId = Number(query.client_id)
                if (Number.isInteger(clientId)) {
                    this.filters.client_id.value = clientId
                    this.clientStore.get(clientId).then(({data}) => {
                        this.clientSearchResults = [data.client]
                    })
                }
            }
            if (typeof query.title === 'string') {
                this.filters.title.value = query.title
            }
            if (typeof query.info === 'string') {
                this.filters.info.value = query.info
            }
            if (query.contract_status_ids) {
                const statusIds = Array.isArray(query.contract_status_ids)
                    ? query.contract_status_ids
                    : query.contract_status_ids.split(',')
                this.filters.contract_status_ids.value = statusIds.map(Number).filter(Number.isInteger)
            }
            if (query.contract_date_from && query.contract_date_to &&
                dayjs(query.contract_date_from).isValid() && dayjs(query.contract_date_to).isValid()) {
                this.filters.contract_date.value = [
                    dayjs(query.contract_date_from).toDate(),
                    dayjs(query.contract_date_to).toDate(),
                ]
            }
        },
        syncFiltersToUrl() {
            const query = {...this.$route.query}
            FILTER_QUERY_KEYS.forEach((key) => delete query[key])

            if (!this.client_id && this.filters.client_id.value) {
                query.client_id = this.filters.client_id.value.toString()
            }
            if (this.filters.title.value?.trim()) {
                query.title = this.filters.title.value.trim()
            }
            if (this.filters.info.value?.trim()) {
                query.info = this.filters.info.value.trim()
            }
            if (this.filters.contract_status_ids.value?.length) {
                query.contract_status_ids = this.filters.contract_status_ids.value.join(',')
            }
            const contractDateRange = this.filters.contract_date.value
            if (contractDateRange?.[0] && contractDateRange?.[1]) {
                query.contract_date_from = dayjs(contractDateRange[0]).format('YYYY-MM-DD')
                query.contract_date_to = dayjs(contractDateRange[1]).format('YYYY-MM-DD')
            }

            this.$router.replace({query})
        },
    },
    watch: {
      value: {
        immediate: true,
        handler() {
          const clientIds = this.value.map((value) => value.client_id)
          this.clientStore.loadClients(clientIds)
        }
      }
    },
}
</script>

<style lang="scss">
.contracts-table {
  --dp-font-family: Changa, sans-serif;
  --dp-font-size: 14px;
  --ms-font-size: 14px;
  --ms-option-font-size: 14px;
  --ms-tag-font-size: 14px;
  --ms-tag-font-weight: 400;

  .form-control,
  .multiselect {
    font-family: Changa, sans-serif;
    font-size: 14px;
  }
}
</style>
